import { AlertTriangle, RefreshCw, ShieldOff, WifiOff } from 'lucide-react';

import { ApiError } from '@nucleus-commerce/api-client/transport';
import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useErrorMessage } from './error-message';

export interface ErrorStateProps {
  error: unknown;
  onRetry?: (() => void) | undefined;
  /** Compact inline variant for table cells and cards. */
  compact?: boolean;
}

const ICONS = {
  network: WifiOff,
  timeout: WifiOff,
  forbidden: ShieldOff,
  unauthorized: ShieldOff,
  default: AlertTriangle,
} as const;

function iconKey(error: unknown): keyof typeof ICONS {
  if (error instanceof ApiError && error.kind in ICONS) return error.kind as keyof typeof ICONS;
  return 'default';
}

/** Inline error rendering for queries. Mutations toast instead; see query/createQueryClient. */
export function ErrorState({ error, onRetry, compact = false }: ErrorStateProps) {
  const { t } = useTranslation('errors');
  const message = useErrorMessage();
  const Icon = ICONS[iconKey(error)];
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  const retryable = !(error instanceof ApiError) || error.retryable || error.kind === 'unknown';

  return (
    <div
      role="alert"
      className={
        compact
          ? 'text-destructive flex items-center gap-2 text-sm'
          : 'border-border bg-card text-card-foreground flex flex-col items-center gap-3 rounded-lg border p-8 text-center'
      }
    >
      <Icon className={compact ? 'size-4 shrink-0' : 'text-muted-foreground size-8'} aria-hidden />
      <div className="space-y-1">
        <p className={compact ? '' : 'font-medium'}>{message(error)}</p>
        {!compact && requestId ? (
          <p className="text-muted-foreground text-xs">
            {t('requestId')}: <code>{requestId}</code>
          </p>
        ) : null}
      </div>
      {onRetry && retryable ? (
        <Button variant={compact ? 'ghost' : 'outline'} size="sm" onClick={onRetry}>
          <RefreshCw aria-hidden />
          {t('retry')}
        </Button>
      ) : null}
    </div>
  );
}
